import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { useAlert } from 'components/message/AlertContext';
import { FormatDate } from 'components/dates/FormatDate';
import { useParams } from 'react-router-dom';
import queryEnvironmentDetails from 'api/Environment/queryEnvironmentDetails';
import { ItemsDetails } from 'components/details/EditorDetails';
import { EnvironmentStage } from 'types/Environment';

export interface TableAccount {
    label: string;
    name: string;
    awsAccount: string;
}

export default function useEnvironmentDetails() {
    const client = useClient() as unknown as {
        query: (query: any) => Promise<any>;
    };
    const { openAlert } = useAlert();
    const params = useParams<{ uri: string }>();

    const [detailsLoading, setDetailsLoading] = useState(true);
    const [items, setItems] = useState<ItemsDetails>({
        created: '',
        label: '',
        description: '',
        data: [],
    });
    const [tableAccounts, setTableAccounts] = useState<TableAccount[]>([]);
    const [detailsError, setDetailsError] = useState(false);

    const formatAWSAccounts = (accounts: {
        [key in EnvironmentStage]?: string;
    }): TableAccount[] => {
        return Object.keys(accounts).reduce((acc: TableAccount[], key) => {
            const accountType = key as EnvironmentStage;
            const awsAccount = accounts[accountType];
            if (awsAccount) {
                acc.push({
                    label: accountType.toUpperCase(),
                    name: accountType,
                    awsAccount,
                });
            }
            return acc;
        }, []);
    };

    const fetchData = async () => {
        try {
            setDetailsLoading(true);
            if (params.uri) {
                const response = await client?.query(
                    queryEnvironmentDetails(params.uri),
                );
                if (!response.detailsErrors) {
                    setItems({
                        created: FormatDate(
                            response.data.getEnvironment.created,
                        ),
                        label: response.data.getEnvironment.label,
                        description: response.data.getEnvironment.description,
                        data: [
                            {
                                label: 'Created by',
                                value: response.data.getEnvironment.owner,
                            },
                            {
                                label: 'Environment URI',
                                value: response.data.getEnvironment
                                    .environmentUri,
                            },
                            {
                                label: 'Organization',
                                value: response.data.getEnvironment.organization
                                    .label,
                                uri: `/organization/${response.data.getEnvironment.organization.organizationUri}`,
                            },
                            {
                                label: 'CloudFormation Stack ID',
                                value: response.data.getEnvironment.stack
                                    .stackUri,
                            },
                            {
                                label: 'Identity Provider Name',
                                value: response.data.getEnvironment
                                    .identityProviderName,
                            },
                            {
                                label: 'Admin groups',
                                value: response.data.getEnvironment
                                    .originSamlGroupName,
                            },
                            {
                                label: 'Developer groups',
                                value: response.data.getEnvironment
                                    .originSamlFullDevGroupName,
                            },
                            {
                                label: 'Reader groups',
                                value: response.data.getEnvironment
                                    .originSamlReaderGroupName,
                            },
                            {
                                label: 'Region',
                                value: response.data.getEnvironment.region,
                            },
                            {
                                label: 'Block Preview to datasets',
                                value: response.data.getEnvironment
                                    .blockpreview,
                            },
                            {
                                label: 'Quicksight enabled',
                                value: response.data.getEnvironment.quicksight_enabled.toString(),
                            },
                            {
                                label: 'Enable environment role overwriting',
                                value: response.data.getEnvironment.role_overwrite_enabled.toString(),
                            },
                            {
                                label: 'Vpc Id',
                                value: response.data.getEnvironment
                                    .defaultPipelineVpcId,
                            },
                        ],
                    });

                    setTableAccounts(
                        formatAWSAccounts({
                            [EnvironmentStage.PROD]:
                                response.data.getEnvironment.AwsProdAccountId,
                            [EnvironmentStage.DEV]:
                                response.data.getEnvironment.AwsDevAccountId,
                            [EnvironmentStage.VAL]:
                                response.data.getEnvironment.AwsValAccountId,
                            [EnvironmentStage.TOOL]:
                                response.data.getEnvironment.AwsToolAccountId,
                            [EnvironmentStage.TEST]:
                                response.data.getEnvironment.AwsTestAccountId,
                        }),
                    );
                } else {
                    setDetailsError(true);
                    openAlert({
                        message: `Something went wrong... Could not retrieve dataset details: ${response.detailsErrors.errors[0]?.message}`,
                        severity: 'alert',
                    });
                }
            }
        } catch (detailsError) {
            setDetailsError(true);
            openAlert({
                message: 'An error occurred while fetching dataset details',
                severity: 'alert',
            });
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        if (client) {
            fetchData();
        }
    }, [client, params.uri]);

    return { items, detailsError, detailsLoading, fetchData, tableAccounts };
}
